import { useEffect, useRef } from "react";

/**
 * Turns an arbitrary backend value into something safe to render.
 *
 * CRMs return relationship fields as nested objects (e.g. Salesforce
 * RecordType: {attributes: {...}, Name: "Client"}) and multi-value fields as arrays.
 * Rendering either as a React child throws (minified error #31), so surface the
 * object's Name/name - or a JSON dump as a last resort - instead.
 *
 * The null/undefined guard is an identity check, not a truthiness check: `value ? ...`
 * anywhere in this path would silently blank out 0 and false.
 *
 * @param {unknown} value - The value to render.
 * @returns {string} - Always a string, never an object, so React can take it as a child.
 */
export const toDisplayValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return "";
  }
  if (Array.isArray(value)) {
    return value.map((item) => toDisplayValue(item)).join("; ");
  }
  if (typeof value === "object") {
    const ref = value as { Name?: unknown; name?: unknown };
    const named = ref.Name ?? ref.name;
    // Name/name is only a display value when it is itself a scalar - a deeper
    // relationship object or a localised {en: "..."} map has to fall through to the
    // JSON dump, or a non-string escapes a function declared to return string.
    if (
      typeof named === "string" ||
      typeof named === "number" ||
      typeof named === "boolean"
    ) {
      return String(named);
    }
    return JSON.stringify(value);
  }
  return String(value);
};

/**
 * toReadableText Utility Function
 *
 * Converts a given snake_case or camelCase text to a human-readable format.
 *
 * @param {string} text - The input text to be converted.
 * @returns {string} - The human-readable version of the input text.
 */
export const toReadableText = (text: string) => {
  // Check if the input text is not empty
  if (text && text.length > 0) {
    // Convert the input text to lowercase
    const allLowercase = text.toLowerCase();

    // Split the text into its components using '_' as the separator
    let textComponents = allLowercase.split(
      allLowercase.includes("_") ? "_" : " "
    );

    // Check if there are multiple components (snake_case)
    if (textComponents && textComponents.length > 1) {
      // Capitalize the first letter of each component and join them with a space
      for (let itemIndex = 0; itemIndex < textComponents.length; itemIndex++) {
        if (textComponents[itemIndex].length > 0) {
          textComponents[itemIndex] =
            textComponents[itemIndex][0].toUpperCase() +
            textComponents[itemIndex].substring(1);
        }
      }
      return textComponents.join(" ");
    }

    // If there's only one component (camelCase), capitalize its first letter and return
    return allLowercase[0].toUpperCase() + allLowercase.substring(1);
  }

  // If the input text is empty or undefined, return an empty string
  return text;
};

/**
 * useTraceUpdate Hook
 *
 * A custom React hook to trace and log updates to a component's props. This hook helps in debugging
 * by logging the changed properties and their previous and current values whenever the component re-renders.
 *
 * @param {Object} props - The component's props that need to be traced.
 * @returns {void} - This hook does not return anything. It rather prints the changes in the console.
 *
 */
export const useTraceUpdate = (props) => {
  // useRef to hold the previous props
  const prev = useRef(props);

  // useEffect to log changed props when the component re-renders
  useEffect(() => {
    // Compare the previous props with the current props and identify changed properties
    const changedProps = Object.entries(props).reduce((ps, [k, v]) => {
      if (prev.current[k] !== v) {
        ps[k] = [prev.current[k], v];
      }
      return ps;
    }, {});

    // If there are any changed props, log them to the console
    if (Object.keys(changedProps).length > 0) {
      console.log("Changed props:", changedProps);
    }

    // Update the previous props with the current props for the next comparison
    prev.current = props;
  });
};

/**
 * agDateColumnFilterComparator Function
 *
 * A comparator function used in Ag-Grid date column filtering to compare a date value in the grid cell
 * with a filter-local date. The function returns a numerical value (1, -1, or 0) based on the comparison
 * result between the two dates.
 *
 * @param {Date} filterLocalDate - The local date used for filtering in the Ag-Grid filter.
 * @param {string} cellValue - The value of the date in the Ag-Grid cell (in string format).
 * @returns {number} - A numerical value indicating the comparison result:
 *                    - 1 if the cellValue date is greater than filterLocalDate.
 *                    - -1 if the cellValue date is less than filterLocalDate.
 *                    - 0 if the cellValue date is equal to filterLocalDate.
 */
export const agDateColumnFilterComparator = (
  filterLocalDate: Date,
  cellValue: string
) => {
  // Convert the cellValue (date string) to a Date object
  let cellValueDate = new Date(cellValue);
  // Set the time of the cellValueDate to 00:00:00.000 (midnight) to ignore the time part for comparison
  cellValueDate.setHours(0, 0, 0, 0);

  // Perform the comparison and return a numerical value based on the result
  if (cellValueDate > filterLocalDate) {
    return 1;
  } else if (cellValueDate < filterLocalDate) {
    return -1;
  } else {
    return 0;
  }
};

/**
 * Number formatter to format numbers US style e.g 123,456
 */
export const NUMBER_FORMATTER = new Intl.NumberFormat("en-US");

const SHORT_DATE_FORMATTERS = new Map<string, Intl.DateTimeFormat>();

/**
 * "23 Apr 2026"-style dates, the one grammar every surface shares. Locale
 * defaults to the reader's own; returns null when the value is missing or
 * does not parse, so each surface chooses its own fallback.
 */
export const formatShortDate = (
  value: string | null | undefined,
  locale?: string
): string | null => {
  if (!value) return null;
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) return null;
  const key = locale ?? "";
  let formatter = SHORT_DATE_FORMATTERS.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
    SHORT_DATE_FORMATTERS.set(key, formatter);
  }
  return formatter.format(parsed);
};

export interface CrmScoreBand {
  min: number;
  max: number;
  color: string;
  label: string;
}

/**
 * The eight-band CRM health score ramp - the product's only traffic-light
 * vocabulary. This is the single definition; gauges, score chips and cell
 * backgrounds all import it rather than re-declaring their own copy. A second
 * copy drifts, and then the same score reads as two different bands on two
 * screens.
 *
 * Bands are half-open [min, max), so a score sitting exactly on a boundary
 * resolves to one band only. The top band is closed at 100 via getCrmScoreBand
 * so a perfect score still lands somewhere.
 */
export const CRM_SCORE_BANDS: CrmScoreBand[] = [
  { min: 0, max: 35, color: "#e50144", label: "Critical" },
  { min: 35, max: 45, color: "#e65100", label: "Poor" },
  { min: 45, max: 55, color: "#f57c00", label: "Fair" },
  { min: 55, max: 65, color: "#ffa000", label: "Average" },
  { min: 65, max: 76, color: "#fbc02d", label: "Good" },
  { min: 76, max: 86, color: "#b5c332", label: "Great" },
  { min: 86, max: 92, color: "#8bc34a", label: "Excellent" },
  { min: 92, max: 100, color: "#34be5d", label: "Outstanding" },
];

/**
 * getCrmScoreBand Function
 * Resolve a 0-100 score to its band. Callers that need a label as well as a
 * colour should use this; getCrmScoreStyles is the shorthand for the common
 * "just tint this cell" case.
 *
 * Note this deliberately keeps the legacy behaviour of treating a missing score
 * as Critical, because existing grids rely on every cell getting a background.
 * A surface that needs to distinguish "not scored" from "scored badly" must
 * check for the absent value itself before calling in.
 *
 * @param {number} score - The score to resolve
 */
export const getCrmScoreBand = (score): CrmScoreBand => {
  if (score >= 100) {
    return CRM_SCORE_BANDS[CRM_SCORE_BANDS.length - 1];
  }
  return (
    CRM_SCORE_BANDS.find((band) => score >= band.min && score < band.max) ||
    CRM_SCORE_BANDS[0]
  );
};

/**
 * getCrmScoreStyles Function
 * Generate a background color CSS prop depending on the score value to indicate traffic light like progression
 * @param {number} score - The score to generate a background color for
 */
export const getCrmScoreStyles = (score) => {
  return { background: getCrmScoreBand(score).color };
};

export const encodeValue = (item) => {
  return encodeURIComponent(item || "");
};

export const isValidUrl = (urlString: string): boolean => {
  try {
    const url = new URL(urlString);
    return ["http:", "https:"].includes(url.protocol);
  } catch {
    return false;
  }
};

export const safeWindowOpen = (url: string, target = "_blank"): void => {
  if (isValidUrl(url)) {
    window.open(url, target, "noopener,noreferrer");
  } else {
    console.warn("Invalid URL blocked:", url);
  }
};

export const isDemoEnvironment = () => {
  return (
    window.location.hostname === "demo.cirrom.com" ||
    window.location.hostname === "demo-eu.cirrom.com" ||
    window.location.hostname === "localhost"
  );
};
